import { reportCacheBridgeFailure } from './cacheBridgeDiagnostics.ts';
import { type CacheBridgeFailureContext } from './cacheBridgeFailureReporter.ts';

export type CacheBridgeCache = 'apollo' | 'relay';

export type CacheWriteResult =
	| { status: 'written' }
	| { status: 'skipped'; reason: string }
	| { status: 'failed'; error: unknown };

export function skipped(reason: string): CacheWriteResult {
	return { status: 'skipped', reason };
}

export function written(): CacheWriteResult {
	return { status: 'written' };
}

export function getSecondaryCache(source: CacheBridgeCache): CacheBridgeCache {
	return source === 'apollo' ? 'relay' : 'apollo';
}

export function getFailureContext(
	bridge: CacheBridgeFailureContext['bridge'],
	operation: CacheBridgeFailureContext['operation'],
	targetCache: CacheBridgeCache,
): CacheBridgeFailureContext {
	return { bridge, operation, targetCache };
}

function getErrorName(error: unknown): string | null {
	return error instanceof Error ? error.name : null;
}

function getErrorMessage(error: unknown): string | null {
	return error instanceof Error ? error.message : null;
}

function isApolloCacheMissError(error: unknown): boolean {
	const name = getErrorName(error);
	const message = getErrorMessage(error);

	if (!message) return false;

	// Apollo Cache 1.x throws this message for incomplete readQuery/readFragment data in
	// development builds.
	if (/^Can't find field .+ on object /.test(message)) return true;

	// Apollo Cache 1.x production builds minify the same missing-field read into
	// InvariantError(8). Keep this exact so unrelated invariant failures still throw.
	return name === 'Invariant Violation' && /^Invariant Violation: 8\b/.test(message);
}

export function shouldFallbackForOriginalReadError(
	source: CacheBridgeCache,
	error: unknown,
): boolean {
	// Relay readers in this package return null for missing store data. If Relay throws,
	// preserve the original error instead of guessing that it was a cache miss.
	if (source === 'relay') return false;

	return isApolloCacheMissError(error);
}

function runSafely<TResult>(
	run: () => TResult,
	context: CacheBridgeFailureContext,
	onError: (error: unknown) => TResult,
): TResult {
	try {
		return run();
	} catch (error) {
		reportCacheBridgeFailure(error, context);
		return onError(error);
	}
}

export function writeSafely(
	write: () => CacheWriteResult,
	context: CacheBridgeFailureContext,
): CacheWriteResult {
	return runSafely(write, context, (error) => ({
		status: 'failed',
		error,
	}));
}

export function readSafely<TData>(
	read: () => TData | null | undefined,
	context: CacheBridgeFailureContext,
): TData | null {
	return runSafely(
		() => read() ?? null,
		context,
		() => null,
	);
}
